import type {
  CampaignCreationCapabilities,
  SmartCreationDraft,
  SmartCreationObjective,
  SmartCreationObjectiveRecommendation,
  SmartCreationRecommendationEvidence,
} from "@adpilot/shared";
import { IconCircleCheck } from "@tabler/icons-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { ConstraintField } from "./objectives-step/ConstraintField";
import { HistoricalReference } from "./objectives-step/HistoricalReference";
import {
  currencySymbol,
  minimumTotalDailyBudget,
  personalityForDraft,
  type RecommendationField,
  recommendationMatchesDraft,
  riskForPersonality,
} from "./objectives-step/objectives-model";
import { PersonalityField } from "./objectives-step/PersonalityField";
import { PromotionWindow } from "./objectives-step/PromotionWindow";
import {
  SelectedProductsContext,
  SelectedProductsWithStatus,
} from "./objectives-step/SelectedProductsContext";
import { reviewScheduleToday } from "./review-schedule";

export type { RecommendationField } from "./objectives-step/objectives-model";

interface ObjectivesStepProps {
  shopId?: string;
  draft: SmartCreationDraft;
  capabilities: CampaignCreationCapabilities;
  recommendation: SmartCreationObjectiveRecommendation | null;
  recommendationPending: boolean;
  productHistory?: SmartCreationRecommendationEvidence | null;
  productHistoryPending?: boolean;
  productHistoryError?: boolean;
  onRefreshProductHistory?: () => void;
  promotionValidationRequested: boolean;
  promotionWindowResetKey: string;
  onDraftChange: (draft: SmartCreationDraft, preserveRecommendation?: boolean) => void;
  onRefreshRecommendation: () => void;
  onApplyRecommendation: (field: RecommendationField) => void;
}

const OBJECTIVES: Array<{
  value: SmartCreationObjective;
  label: "driveGrowth" | "optimizeRoas" | "launchProduct" | "promotion";
}> = [
  { value: "optimize_roas", label: "optimizeRoas" },
  { value: "drive_growth", label: "driveGrowth" },
  { value: "launch_product", label: "launchProduct" },
  { value: "promotion_sales_boost", label: "promotion" },
];

export function ObjectivesStep({
  shopId,
  draft,
  capabilities,
  recommendation,
  recommendationPending,
  productHistory,
  productHistoryPending = false,
  productHistoryError = false,
  onRefreshProductHistory,
  promotionValidationRequested,
  promotionWindowResetKey,
  onDraftChange,
  onRefreshRecommendation,
  onApplyRecommendation,
}: ObjectivesStepProps) {
  const { t } = useTranslation("smartCreation");
  const currentRecommendation = recommendationMatchesDraft(draft, recommendation);
  const minimumBudget = minimumTotalDailyBudget(draft, capabilities);
  const personality = personalityForDraft(draft);
  const promotionWindowReady =
    draft.objective !== "promotion_sales_boost" || draft.promotionDateRange !== null;
  const refreshRecommendationRef = useRef(onRefreshRecommendation);

  useEffect(() => {
    refreshRecommendationRef.current = onRefreshRecommendation;
  }, [onRefreshRecommendation]);

  useEffect(() => {
    const today = reviewScheduleToday(new Date());
    if (draft.startDate >= today && (!draft.endDate || draft.endDate >= today)) return;
    onDraftChange({
      ...draft,
      startDate: today,
      endDate: draft.endDate && draft.endDate >= today ? draft.endDate : null,
    });
  }, [draft, onDraftChange]);

  // Auto-refresh at most once per draft: if that attempt fails (or yields no current
  // recommendation) we wait for the user to change an input instead of retrying in a loop.
  const autoRefreshedDraftRef = useRef<SmartCreationDraft | null>(null);

  useEffect(() => {
    if (currentRecommendation) {
      autoRefreshedDraftRef.current = null;
      return;
    }
    if (!draft.objective || recommendationPending || !promotionWindowReady) return;
    if (autoRefreshedDraftRef.current === draft) return;
    const timer = window.setTimeout(() => {
      autoRefreshedDraftRef.current = draft;
      refreshRecommendationRef.current();
    }, 450);
    return () => window.clearTimeout(timer);
  }, [draft, currentRecommendation, promotionWindowReady, recommendationPending]);

  return (
    <div className="flex flex-col gap-4">
      <FieldSet>
        <FieldLegend variant="label">{t("objectives.campaignObjective")}</FieldLegend>
        <ToggleGroup
          variant="outline"
          value={draft.objective ? [draft.objective] : []}
          onValueChange={(values) => {
            const objective = (values.find((value) => value !== draft.objective) ?? values[0]) as
              | SmartCreationObjective
              | undefined;
            if (!objective) return;
            onDraftChange({
              ...draft,
              objective,
              promotionDateRange:
                objective === "promotion_sales_boost" ? draft.promotionDateRange : null,
            });
          }}
          className="grid w-full grid-cols-2 gap-2.5 xl:grid-cols-4"
          aria-label={t("objectives.campaignObjective")}
        >
          {OBJECTIVES.map((objective) => (
            <ToggleGroupItem
              key={objective.value}
              value={objective.value}
              className="h-[78px] w-full flex-col items-stretch justify-start gap-1 rounded-[9px] p-3 text-left whitespace-normal aria-pressed:border-ink aria-pressed:bg-surface aria-pressed:ring-1 aria-pressed:ring-ink"
            >
              <span className="flex w-full items-center justify-between gap-3">
                <span className="font-semibold text-ink">{t(`objectives.${objective.label}`)}</span>
                {draft.objective === objective.value ? (
                  <IconCircleCheck className="size-4" aria-hidden="true" />
                ) : null}
              </span>
              <span className="text-xs font-normal leading-4 text-muted-foreground">
                {t(`objectives.${objective.label}Description`)}
              </span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </FieldSet>

      <section
        className={cn(
          "grid gap-2.5",
          draft.objective === "promotion_sales_boost" ? "lg:grid-cols-4" : "lg:grid-cols-3",
        )}
        aria-label={t("objectives.constraints.title")}
      >
        <ConstraintField
          field="targetAcos"
          label={t("objectives.targetAcos")}
          value={draft.targetAcos === null ? "" : Number((draft.targetAcos * 100).toFixed(2))}
          min={1}
          max={200}
          step={0.1}
          suffix="%"
          recommendation={draft.objective ? recommendation : null}
          recommendationPending={Boolean(draft.objective) && recommendationPending}
          recommendationCurrent={currentRecommendation}
          capabilities={capabilities}
          minimumBudget={minimumBudget}
          onChange={(value) =>
            onDraftChange(
              { ...draft, targetAcos: Number.isFinite(value) ? value / 100 : null },
              true,
            )
          }
          onApply={() => onApplyRecommendation("targetAcos")}
        />
        <ConstraintField
          field="totalDailyBudget"
          label={t("objectives.dailyBudget")}
          value={draft.totalDailyBudget ?? ""}
          min={minimumBudget}
          step={1 / 10 ** capabilities.dailyBudget.decimals}
          prefix={currencySymbol(capabilities.currencyCode)}
          recommendation={draft.objective ? recommendation : null}
          recommendationPending={Boolean(draft.objective) && recommendationPending}
          recommendationCurrent={currentRecommendation}
          capabilities={capabilities}
          minimumBudget={minimumBudget}
          onChange={(value) =>
            onDraftChange(
              { ...draft, totalDailyBudget: Number.isFinite(value) ? value : null },
              true,
            )
          }
          onApply={() => onApplyRecommendation("totalDailyBudget")}
        />
        {draft.objective === "promotion_sales_boost" ? (
          <PromotionWindow
            key={promotionWindowResetKey}
            draft={draft}
            validationRequested={promotionValidationRequested}
            onDraftChange={onDraftChange}
          />
        ) : null}
        <PersonalityField
          value={personality}
          onChange={(value) =>
            onDraftChange({
              ...draft,
              aiPersonality: value,
              riskPreference: riskForPersonality(value),
            })
          }
        />
      </section>

      {shopId ? (
        <SelectedProductsWithStatus draft={draft} shopId={shopId} />
      ) : (
        <SelectedProductsContext draft={draft} />
      )}

      <HistoricalReference
        capabilities={capabilities}
        recommendation={recommendation}
        evidence={productHistory ?? recommendation?.evidence}
        pending={productHistoryPending}
        failed={productHistoryError}
        onRefresh={onRefreshProductHistory ?? onRefreshRecommendation}
      />
    </div>
  );
}
